import React, { useState } from 'react';
import { ActivityIndicator, Switch, Text, TextInput, View } from 'react-native';
import { AppPressable } from './AppPressable';
import { BottomSheet, type BottomSheetPalette } from './BottomSheet';
import { ThemePickerRow, type ChipPalette } from './ThemePickerRow';
import { useLocale } from '../i18n/LocaleContext';
import { useVisualTheme } from '../theme/VisualThemeContext';
import type { ChatListStyles } from '../theme/chatListStylesFromPalette';
import { STATUS_TEXT_MAX_CHARS } from '../util/inputLimits';
import { humanizeError } from '../util/humanizeError';
import { notifyAction } from '../util/confirmDialog';
import { showToast } from '../util/toastBridge';

/**
 * Лист «Быстрые настройки» — по тапу на своё имя в шапке списка чатов.
 *
 * <p><b>Зачем.</b> Имя владельца в шапке было мёртвым: нажималась только аватарка рядом, а
 * сама строка не делала ничего. При этом здесь собрано ровно то, что человек трогает чаще
 * раза в месяц, — статус, тишина и вид приложения, — а лежало оно в окне настроек по разным
 * вкладкам, статус вдобавок под общей кнопкой «Сохранить» вместе с логином и датой рождения.
 *
 * <p><b>Почему вместе.</b> Все три отвечают на один вопрос — «как я сейчас присутствую»:
 * что обо мне написано, слышно ли меня, как это выглядит. Разнести их значило бы завести
 * человеку три места вместо одного и правило «а вот это где-то ещё», которое нечем объяснить.
 *
 * <p>Тема и тишина сохраняются сами: первая — локально (`setVisualTheme`), вторая — своей
 * серверной ручкой. Кнопка «Сохранить» относится только к полю статуса: профильная ручка одна
 * на все поля профиля, и дёргать её на каждую букву не за чем.
 */
type Props = {
  visible: boolean;
  onClose: () => void;
  styles: ChatListStyles;
  sheetPalette: BottomSheetPalette;
  themeChipPalette: ChipPalette;
  /** Что сохранено сейчас; `null` — статуса нет. */
  statusText: string | null | undefined;
  /** Сохранение на сервере и в профиле устройства — снаружи, вместе с остальными полями. */
  onSave: (statusText: string | null) => Promise<void>;
  /** «Не беспокоить»: состояние с сервера. */
  doNotDisturb: boolean;
  /** Тумблер сохраняется сам, своей ручкой — отсюда отдельный колбэк, а не общий `onSave`. */
  onToggleDoNotDisturb: (next: boolean) => Promise<void>;
};

export function SelfStatusSheet({
  visible,
  onClose,
  styles,
  sheetPalette,
  themeChipPalette,
  statusText,
  onSave,
  doNotDisturb,
  onToggleDoNotDisturb,
}: Props) {
  const { t } = useLocale();
  const { visualTheme, setVisualTheme } = useVisualTheme();

  const [draft, setDraft] = useState(statusText ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Положение тумблера, пока сервер не ответил. Без него переключатель отскакивал бы назад до
   * конца запроса: собственный ответ на нажатие важнее, чем полсекунды честности.
   */
  const [dndDraft, setDndDraft] = useState(doNotDisturb);
  const [dndBusy, setDndBusy] = useState(false);
  const [dndError, setDndError] = useState<string | null>(null);
  // Настройка серверная и общая для устройств: вне запроса тумблер показывает то, что на
  // сервере, — пришла правка с другого устройства или отказ на этом, он идёт за ней.
  const dndShown = dndBusy ? dndDraft : doNotDisturb;

  /**
   * Каждое открытие начинается с сохранённого значения. Брошенный черновик — это как раз то,
   * чего человек не захотел: показать его снова означало бы, что закрытие листа ничего не
   * отменило.
   */
  const [syncedWith, setSyncedWith] = useState<{
    visible: boolean;
    statusText: typeof statusText;
    doNotDisturb: boolean;
  } | null>(null);
  if (
    syncedWith?.visible !== visible ||
    syncedWith.statusText !== statusText ||
    syncedWith.doNotDisturb !== doNotDisturb
  ) {
    setSyncedWith({ visible, statusText, doNotDisturb });
    if (visible) {
      setDraft(statusText ?? '');
      setError(null);
      setBusy(false);
      setDndError(null);
    }
  }

  const saved = (statusText ?? '').trim();
  const next = draft.trim();

  async function save() {
    if (busy) return;
    if (next === saved) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Пустое поле — это снятие статуса, а не пустая строка: в профиле его не должно остаться.
      await onSave(next === '' ? null : next);
      onClose();
    } catch (e) {
      setError(humanizeError(e, t('profileUpdateFailed')));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Закрытие листа мимо кнопки — смахиванием, тапом по фону, «назад» (аудит удобства
   * 2026-10-04, п. 14). Правило то же, что у «Готово» в настройках: набранное сохраняется, а не
   * пропадает молча. Лист к этому моменту уже уехал, поэтому итог сообщаем уведомлением, а
   * отказ — окном: ошибку человек должен заметить, а не пропустить.
   */
  function closeSavingDraft() {
    onClose();
    if (busy || next === saved) return;
    void onSave(next === '' ? null : next)
      .then(() => {
        showToast({ message: t('selfStatusSavedToast') });
      })
      .catch((e: unknown) => {
        void notifyAction({ title: humanizeError(e, t('profileUpdateFailed')), okText: t('close') });
      });
  }

  async function toggleDnd(next: boolean) {
    setDndDraft(next);
    setDndBusy(true);
    setDndError(null);
    try {
      await onToggleDoNotDisturb(next);
    } catch (e) {
      // Назад к тому, что на сервере, тумблер вернётся сам (см. `dndShown`): оставшийся в
      // новом положении после отказа, он сообщал бы о тишине, которой нет.
      setDndError(humanizeError(e, t('dndFailed')));
    } finally {
      setDndBusy(false);
    }
  }

  return (
    <BottomSheet
      visible={visible}
      onClose={closeSavingDraft}
      palette={sheetPalette}
      title={t('quickSettingsTitle')}>
      <Text style={styles.settingsFieldLabel}>{t('settingsStatusLabel')}</Text>
      {/*
        Поле и «Сохранить» — в одну строку (п. 14): кнопка рядом с тем, что сохраняет, и лист
        ниже на целый ряд. «Готово» на клавиатуре сохраняет так же, как кнопка.
      */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <TextInput
          placeholder={t('settingsStatusPlaceholder')}
          value={draft}
          onChangeText={setDraft}
          style={[styles.input, { flex: 1 }]}
          maxLength={STATUS_TEXT_MAX_CHARS}
          editable={!busy}
          returnKeyType="done"
          onSubmitEditing={() => void save()}
          accessibilityLabel={t('settingsStatusLabel')}
        />
        <AppPressable
          style={[styles.modalBtn, styles.modalPrimary]}
          onPress={() => void save()}
          disabled={busy}
          accessibilityRole="button"
          accessibilityLabel={t('selfStatusSave')}>
          {busy ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={[styles.modalBtnText, { color: '#fff' }]}>{t('selfStatusSave')}</Text>
          )}
        </AppPressable>
      </View>
      {error ? (
        <Text style={styles.dmErr} accessibilityLiveRegion="assertive" accessibilityRole="alert">
          {error}
        </Text>
      ) : null}

      <View style={styles.settingsSectionDivider} />

      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text style={[styles.settingsFieldLabel, { flex: 1, paddingRight: 12 }]}>
          {t('dndLabel')}
        </Text>
        <Switch
          value={dndShown}
          onValueChange={(next) => void toggleDnd(next)}
          disabled={dndBusy}
          accessibilityLabel={t('dndLabel')}
        />
      </View>
      <Text style={styles.settingsInlineHint}>{t('dndHint')}</Text>
      {dndError ? <Text style={styles.dmErr}>{dndError}</Text> : null}

      <View style={styles.settingsSectionDivider} />

      <Text style={styles.settingsFieldLabel}>{t('settingsVisualThemeLabel')}</Text>
      <ThemePickerRow
        visualTheme={visualTheme}
        onSelect={setVisualTheme}
        palette={themeChipPalette}
      />
    </BottomSheet>
  );
}
